import {
  type Appointment,
  type Customer,
  type DesignsInfo,
  type DrawingTypes,
  type NbaObject,
  type NbaType,
  type OpportunityDrawing,
  type Order,
} from "@repo/api";

import { type StagesEnum } from "./stagesEnum";

export const HFBKeys = [
  "hfb01", //  "Living room seating"
  "hfb02", //  "Store and organise furniture"
  "hfb03", //  "Workspaces"
  "hfb04", //  "Bedroom furniture"
  "hfb05", //  "Beds & Mattresses"
  "hfb06", //  "Bathroom"
  "hfb07", //  "Kitchen"
  "hfb08", //  "Dining"
  "hfb09", //  "IKEA for Kids"
  "hfb10", //  "Lighting"
  "hfb11", //  "Bed and bath textiles"
  "hfb12", //  "Home textiles"
  "hfb13", //  "Rugs"
  "hfb14", //  "Cooking"
  "hfb15", //  "Eating"
  "hfb16", //  "Decoration"
  "hfb17", //  "Outdoor"
  "hfb18", //  "Home Organization"
  "hfb19", //  "Secondary storage"
  "hfb50", //  "Home electronics"
  "hfb70", //  "Home Appliances"
  "hfb96", //  "IKEA Food"
  "hfb97", //  "Services"
  "hfb1001", //  "Customer Service"
] as const;

export type hfb = (typeof HFBKeys)[number];

export const ServiceKeys = {
  1: "MEASUREMENT_SERVICE", // "Measurement service",
  2: "HOME_PLANNING", // "Home planning",
  3: "HOME_DELIVERY", // "Home delivery",
  5: "PICKING_SERVICE", // "Picking service",
  6: "ASSEMBLY_SERVICE", // "Assembly service",
  7: "INSTALLATION", // "Installation",
  8: "STORE_APPOINTMENT", // "Store appointment",
  9: "BUSINESS_SERVICES", // "Business services",
  10: "FINANCIAL_SUPPORT", // "Financial support",
  11: "REMOTE_PLANNING", // "Remote planning",
  12: "INTERIOR_DESIGN", // "Interior design",
  13: "COLLECT_DELIVERY", // "Collect Delivery",
  14: "PLANNING_KITCHEN_STORE", // "Kitchen planning - Store",
  15: "PLANNING_KITCHEN_REMOTE", // "Kitchen planning - Remote",
  16: "PLANNING_KITCHEN_HOME", // "Kitchen planning - Home",
  17: "PLANNING_WARDROBE_STORE", // "Wardrobe planning - Store",
  18: "PLANNING_WARDROBE_REMOVE", // "Wardrobe planning - Remote",
  19: "CUSTOM_WORKTOP", // "Custom Worktop",
  20: "BUSINESS_CONSULTATION_REMOTE", // "Business consultation remote",
  21: "PLANNING_BATHROOM_REMORE", // "Planning bathroom - Remote",
  22: "PLANNING_SLEEP_REMOTE", // "Planning sleep - Remote",
  23: "PLANNING_BATHROOM_STORE", // "Planning bathroom - Store",
  24: "CONTROL_MEASUREMENT", // "Control measurement",
  25: "PLANNING_SLEEP", // "Planning sleep",
  26: "REMOVAL_SERVICES", // "Removal services",
} as const;

export type service = keyof typeof ServiceKeys;

export interface OpportunityOrder {
  orderId: string;
  createdTime: string;
  updatedTime?: string;
  status?: string;
  updated?: boolean;
  isDraftOrder?: boolean;
  isIncludedInOppValue: boolean;
  waitingListOrder?: boolean;
}

export interface UpdateOpportunity {
  id: Opportunity["id"];
  status?: Opportunity["status"];
  customerBudget?: Opportunity["customerBudget"];
  classification?: Opportunity["classification"];
  title?: Opportunity["title"];
  hfb?: Opportunity["hfb"];
  owner?: Opportunity["owner"];
  assignedTeam?: Opportunity["assignedTeam"];
  tags?: Opportunity["tags"];
  customerMeetingPointBuCode?: Opportunity["customerMeetingPointBuCode"];
  classificationChangeDetails?: {
    reason?: string;
    description?: string;
  };
  expectedPurchase?: Opportunity["expectedPurchase"];
  servicesInterested?: Opportunity["servicesInterested"];
  interestArea?: Opportunity["interestArea"];
  statusChangeDetails?: {
    reason: string;
    description?: string;
  };
  allowContact?: Opportunity["allowContact"];
  consentChangeReason?: string;
  action?: {
    comment?: string;
    orderNo?: string;
    action: ActionKind;
    reason?: Reason["kind"];
    quotationId?: string;
  };
  isPinned?: boolean;
  dropInAppointmentId?: string;
}

export interface QuotationInfo {
  id: string;
  status: string;
  amount: number;
  title: string;
  createdTime: string;
  updatedTime: Date;
  createdBy: string;
  noOfItems: number;
  buCode: string;
  imageUrl: string;
  expiryDate: string;
  currencyCode: string;
  isIncludedInOppValue?: boolean;
}

export type OpportunityType =
  | "Blocked Order"
  | "Sales Opportunity"
  | "Boka"
  | "ENR"
  | "Quotation"
  | "Assistance Request";

export enum OpportunityStatusEnum {
  Unassigned = "Unassigned",
  Assigned = "Assigned",
  InProgress = "InProgress",
  ClosedWon = "ClosedWon",
  ClosedLost = "ClosedLost",
  Invalid = "Invalid",
}

export type sourceReasonCodeType =
  | "PIANOTPAID"
  | "MULTIPLE_ORDERS_GIVE_LOW_LIMIT"
  | "LOWLIMIT"
  | "CARDBLOCK"
  | "CANC_PIA_DATE_PASSED"
  | "NOT_PAID_ON_CRE_DATE"
  | "CREDIT_CARD_REJECTED"
  | "PAYM_GATEWAY_UNAVAIL"
  | "CUSTPAYM"
  | "NEWLEAD";

export const isOpportunityStatus = (
  status: string
): status is OpportunityStatusEnum => {
  return Object.values(OpportunityStatusEnum)
    .map((value) => value.toLowerCase())
    .includes(status.replaceAll("_", "").toLowerCase());
};

export interface Opportunity {
  id: number;
  externalId: number;
  source: "SLA" | "iSell";
  assignedTo: string;
  status: OpportunityStatusEnum;
  remark?: string;
  title: string;
  interestArea?: string;
  hfb?: hfb[];
  countryCode?: string;
  servicesInterested?: service[];
  buName?: string;
  customerMeetingPointName?: string;
  buType?: string;
  buCode?: string;
  customerMeetingPointBuCode: string;
  customerMeetingPointBuName: string;
  city?: string;
  sourceReasonCode:
    | "PIANOTPAID"
    | "MULTIPLE_ORDERS_GIVE_LOW_LIMIT"
    | "LOWLIMIT"
    | "CARDBLOCK"
    | "CANC_PIA_DATE_PASSED"
    | "NOT_PAID_ON_CRE_DATE"
    | "CREDIT_CARD_REJECTED"
    | "PAYM_GATEWAY_UNAVAIL"
    | "CUSTPAYM"
    | "NEWLEAD";
  orderInfo: {
    blockedAt: string;
    orderType: "SALESORDER" | "DISTR" | "PIANOTPAID";
    amount: number;
    items: string[];
    createdAt: string;
    updatedAt: string;
    orderNo: string;
  };
  customerBudget?: string;
  customerName?: string;
  customerFirstName?: string;
  customerLastName?: string;
  customerLocale?: string;
  owner?: string;
  assignedTeam?: number;
  partyId?: string;
  allowContact: boolean;
  country: string;
  type: OpportunityType;
  opportunityType?: OpportunityType; // only in project search API
  classification: ClassificationDefinition;
  sourceSystem: string;
  businessUnit: {
    buCode: string;
    buType: string;
    buName: string;
  };
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
  attachments?: Attachment[];
  orders?: {
    allOrders?: OpportunityOrder[];
  };
  planners: {
    plannersInfo: OpportunityDrawing[];
    designsInfo: DesignsInfo[];
  };

  amount?: number;
  displayAmount?: number;
  inprogressAmount?: number;
  closedWonAmount?: number;
  closedLostAmount?: number;
  currencySymbol?: string;
  currency: string;
  blockedAt?: string;
  expectedPurchase?: string;
  appointmentDetails?: Appointment[];
  lockStatus?: boolean;
  tags?: string[];
  systemGeneratedTags?: string[];
  nbas?: NbaObject[];
  isPinned?: boolean;
  customerType?: Customer["customerType"];
  organizationName?: string;
  quotations?: {
    quotationInfo?: QuotationInfo[];
  };
  customerProjects?: CustomerProject[];
  prePlanningDetails?: PrePlanningDetail[];
  installationQuotes?: {
    installationQuotationInfo?: InstallationQuotationData[];
  };
  serviceWorkOrders?: {
    serviceWorkOrderInfo?: ServiceWorkOrderData[];
  };
  stageData?: {
    stage: StagesEnum;
    manualOverride: boolean;
  };
  aiProjectData: {
    nextBestActions: NextBestAction[];
    summaries: ProjectSummaries;
    summaryGeneratedAt: string | null;
  } | null;
}

export interface ServiceWorkOrderData {
  id?: string;
  workType?: string;
  kitchenPlannerLink?: string;
  scheduledStartTime?: string;
  scheduledEndTime?: string;
  workOrderNumber?: string;
  serviceOrderNumber?: string;
  serviceResource?: string;
  opportunityExternalId?: string;
  workOrderExternalId?: string;
  measurementFilesUploaded?: boolean;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CustomerProject {
  id: string;
  name: string;
  updatedAt: string;
  homeFurnishingBusinesses: hfb[];
  appointmentId?: number;
}

export interface PrePlanningDetail {
  planningId: string; // system generated id
  source: string; // "KREATIV" | "DAM" | "HIP";
  sourceId: string; // id from the source system
  customerProjectId: string;
  sourceAddedAt: string;
  planningType: "MEASUREMENT" | "INSPIRATION" | "IMAGINATION";
  kreativScanUrl?: string | null; // Only for measurements
  gpcPlanUrl?: string; // Only for GPC
  planCode?: string; // GPC plan code
  imaginationPlanningUrl?: string;
  teaserImageUrl?: string;
}

type LanguageCode = string;
type SummaryTranslation = Record<LanguageCode, string>;

export interface OpportunitySearch {
  opportunityId: number;
  // deprecated, use customerFirstName and customerLastName instead
  customerName: string;
  customerFirstName: string;
  customerLastName: string;
  classification: number;
  amount?: number;
  opportunityType: OpportunityType;
  title: string;
  buCode: string;
  buType: string;
  buName: string;
  customerMeetingPointName: string;
  sourceSystem: string;
  sourceReasonCode: sourceReasonCodeType;
  status: OpportunityStatusEnum;
  assignedTo: string;
  countryCode: string;
  createdAt: string;
  updatedAt: string;
  estimatedBudget: string;
  followUpConsent: boolean;
  promotionalConsent: boolean;
  hfbs: hfb[];
  currency: string;
  currencySymbol: string;
  customerType: Customer["customerType"];
  organizationName: string;
  lockStatus: boolean;
  inprogressAmount: number | null;
  closedWonAmount: number | null;
  closedLostAmount: number | null;
  displayAmount: number | null;
  nbas: NbaObject[];
  assignedTeam: number | null;
  customerLocale: string;
  isPinned: boolean;
  registeredType: string;
  isNoActionPlanned: boolean;
  isColdProject: boolean;
  followupAssignee?: string;
  followupSummary?: {
    id: string;
    action: SummaryTranslation;
    insight: SummaryTranslation;
    talkingPoint: SummaryTranslation;
  };
}

export interface OpportunitiesSearchResponse {
  content: OpportunitySearch[];
  pageable: {
    sort: { sorted: boolean; unsorted: boolean; empty: boolean };
    offset: number;
    pageNumber: number;
    pageSize: number;
    paged: boolean;
    unpaged: boolean;
  };
  totalPages: number;
  totalElements: number;
  last: boolean;
  size: number;
  number: number;
  sort: { sorted: boolean; unsorted: boolean; empty: boolean };
  numberOfElements: number;
  first: boolean;
  empty: boolean;
  brief?: CoworkerBriefText; // AI-generated projects brief
}

export interface OpportunitiesSearchOptions {
  pageIndex: number;
  pageSize: number;
  sortKey: string;
  sortOrder: "asc" | "desc";
  isPinned?: boolean;
  refreshResult?: boolean;
}

export interface OpportunitiesSearchFilters {
  isWidgetSearch?: boolean; // When this is true the BE will not save the last query in user profile
  term?: string;
  amount?: number[];
  createdDate?: (Date | undefined)[];
  updatedDate?: (Date | undefined)[];
  opportunityType?: OpportunityType[];
  classification?: ClassificationDefinition[];
  status?: string[];
  department?: hfb[];
  businessUnit?: string[];
  customerType?: Customer["customerType"][];
  services?: number[];
  assigned?: boolean;
  followUpConsentFilter?: string;
  promotionalConsentFilter?: string;
  teamFilter?: number;
  assignedToTeam?: boolean;
  coworkerFilter?: string[];
  tags?: string[];
  nbaTypes?: NbaType[];
  orderStatus?: Order["orderStatus"][];
  customerLocales?: string[];
  orderMethod?: string[];
  bokaServices?: string[];
  installationQuotationStatus?: InstallationQuotationStatus[];
  installationQuotationExists?: boolean;
  workOrderStatus?: WorkOrderStatus[];
  workOrderType?: WorkOrderType[];
  isNoActionPlanned?: boolean;
  isColdProject?: boolean;
  followupAssignee?: string; // ai-generated project follow-up assignee email
  projectIds?: string[];
  installationType?: InstallationType[];
  installationDate?: (Date | undefined)[];
  stageFilter?: string[];
  waitingList?: boolean;
  expectedPurchaseDateFilter?: (Date | undefined)[];
  budgetFilter?: number[];
}

export interface OpportunityCustomer {
  consents: {
    privacyPolicy: boolean;
    termsAndCondition: boolean;
  };
  customer: Customer;
}

export type ClassificationDefinition = "Urgent" | "High" | "Medium" | "Low";
export type ClassificationValue = 1 | 2 | 3 | 4;

export enum AttachmentScanStatus {
  WAITING_FOR_SCAN = "WAITING_FOR_SCAN",
  CLEAN = "CLEAN",
  INFECTED = "INFECTED",
}

export interface Attachment {
  name: string;
  uri: string;
  size: number;
  type?: string;
  uploaded_date: string;
  uploaded_user: string;
  attachmentId: number;
  isSharedWithCustomerProject?: boolean;
  scanStatus?: AttachmentScanStatus | null;
}

export type ActionReason = Channel["kind"] | Reason["kind"] | ActionKind;

interface Channel {
  kind: "CUSTOMER_CALLED_IN" | "CALLED_THE_CUSTOMER" | "WALK_IN_CUSTOMER";
  name: string;
  actionType: number;
}
interface Reason {
  kind:
    | "IMPORT_PLANNER"
    | "SEND_APPOINTMENT_REMAINDER"
    | "SEND_PAYMENT_REMAINDER"
    | "SEND_SCHEDULED_PHONE_CALL_REMAINDER"
    | "MODIFY"
    | "CANCEL"
    | "CREATE_NEW_ORDER"
    | "IMPORT_ORDER"
    | "OTHER_PLANNER";
  name: string;
  actionType: number;
  types: DrawingTypes[];
}

type ActionKind =
  | "MANAGE_PLANNER"
  | "SPOKE_WITH_CUSTOMER"
  | "SCHEDULE_CALL"
  | "OTHER_ACTION_TAKEN"
  | "SEND_REMAINDER"
  | "MANAGE_ORDER"
  | "SCHEDULE_AN_APPOINTMENT"
  | "IMPORT_QUOTATION";

export interface Action {
  action?: ActionReason;
  reason?: ActionReason;
  comment?: string;
  appointment?: string;
  title?: string;
  orderNo?: string;
  plannerId?: string;
}

export type OrderMethod =
  | "CHAT"
  | "CUST_SPT"
  | "DIGITAL_MEETING"
  | "FAX"
  | "INTERNET"
  | "LETTER"
  | "OUTBOUND_LEAD_SALES"
  | "OUTBOUND_PHONE"
  | "PHONE"
  | "PROACTIVE_CHAT"
  | "REMOTE_BATHROOM"
  | "REMOTE_BEDROOM"
  | "REMOTE_EMAIL_SALES"
  | "REMOTE_INT_DESIGN"
  | "REMOTE_KITCHEN"
  | "REMOTE_LIVINGROOM"
  | "REMOTE_OTHER_RANGE"
  | "SOCIAL_MEDIA"
  | "STORE"
  | "STORE_KITCHEN"
  | "TELANSMACH"
  | "WRITTEN";

export type NewProjectType = "SLA";

export interface InstallationQuotationData {
  id: number;
  drawingId?: number;
  quoteExternalId?: string;
  quoteNumber?: string;
  opportunityExternalId?: string;
  status?: string;
  docusignDateSent?: string;
  installationStartDate?: string;
  installationEndDate?: string;
  depositPaid?: boolean;
  grandTotal?: number;
}

export type InstallationQuotationStatus =
  "DRAFT" | "SENT" | "ACCEPTED" | "VOIDED" | "NOTSIGNED";

export enum WorkOrderStatus {
  New = "New",
  InProgress = "In Progress",
  MeasurementsComplete = "Measurements Complete",
  IHPComplete = "IHP Complete",
  PlanComplete = "Plan Complete",
  Completed = "Completed",
  Closed = "Closed",
  Cancelled = "Cancelled",
  CannotComplete = "Cannot Complete",
}

export const workOrderStatusList: WorkOrderStatus[] = [
  WorkOrderStatus.New,
  WorkOrderStatus.InProgress,
  WorkOrderStatus.MeasurementsComplete,
  WorkOrderStatus.IHPComplete,
  WorkOrderStatus.PlanComplete,
  WorkOrderStatus.Completed,
  WorkOrderStatus.Closed,
  WorkOrderStatus.Cancelled,
  WorkOrderStatus.CannotComplete,
];

export enum WorkOrderType {
  InstallConsecutiveDays = "Install (Consecutive Days)",
  IKEA_Measure_US = "IKEA Measure (US)",
  IKEA_MeasureAndPlan_US = "IKEA Measure & Plan (US)",
  PreInstallationVisit_US = "Pre Installation Visit (US)",
  GoBack = "Go Back",
  Warranty = "Warranty",
  ShowRoomWork = "Show Room Work",
  InHomeKitchenPlanning = "In Home Kitchen Planning (US)",
}

export const workOrderTypeList: WorkOrderType[] = [
  WorkOrderType.InstallConsecutiveDays,
  WorkOrderType.IKEA_Measure_US,
  WorkOrderType.IKEA_MeasureAndPlan_US,
  WorkOrderType.PreInstallationVisit_US,
  WorkOrderType.GoBack,
  WorkOrderType.Warranty,
  WorkOrderType.ShowRoomWork,
  WorkOrderType.InHomeKitchenPlanning,
];

export enum NextBestAction {
  ASSIGN_PROJECT = "ASSIGN_PROJECT",
  SCHEDULE_APPOINTMENT = "SCHEDULE_APPOINTMENT",
  PRE_CALL = "PRE_CALL",
  NEED_ANALYSIS_QUESTIONNAIRE = "NEED_ANALYSIS_QUESTIONNAIRE",
  ATTACH_PLANNER = "ATTACH_PLANNER",
  BOOK_FOLLOW_UP_APPOINTMENT = "BOOK_FOLLOW_UP_APPOINTMENT",
  ATTACH_QUOTE_OR_ORDER = "ATTACH_QUOTE_OR_ORDER",
  ATTACH_INSTALLATION_OR_SERVICE = "ATTACH_INSTALLATION_OR_SERVICE",
  FOLLOW_UP = "FOLLOW_UP",
  UPLOAD_FILES = "UPLOAD_FILES",
  UPDATE_BUDGET = "UPDATE_BUDGET",
  UPDATE_EXPECTED_PURCHASE_DATE = "UPDATE_EXPECTED_PURCHASE_DATE",
}

export interface ProjectSummaries {
  en?: string;
  [key: string]: string | undefined;
}

export interface CoworkerPortfolioBriefResponse {
  requestId: number;
  status: string;
  response: CoworkerPortfolioBriefData | null;
  filters: string | null; // JSON string
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface CoworkerPortfolioBriefData {
  brief: CoworkerBriefText;
  actions: CoworkerBriefAction[];
  projectCategorisation: ProjectCategorisation[];
  generatedAt: string;
}

export type CoworkerBriefText = Record<string, string>;

export interface CoworkerBriefAction {
  projectId: string;
  action: Record<string, string>;
}

export interface ProjectCategorisation {
  category: Record<string, string>;
  projectIds: string[];
}

export enum InstallationType {
  KITCHEN = "KITCHEN",
}
